import { when } from "mobx";
import { SaveState } from "./document";
/** How long to wait for an in-flight save before giving up and proceeding anyway. */
export const kWaitForSaveSettledCapMs = 3000;
function isSettled(document) {
    return document.saveState !== SaveState.Saving && document.saveState !== SaveState.Retrying;
}
/**
 * Waits until `document` is no longer mid-save, so a request that reads it (an Ideas click writing
 * the evaluation timestamp) is more likely to see what the student just saved.
 *
 * Resolves immediately on `Idle` or `Saved`. Does not wait for `Saved` specifically: `SaveIndicator`
 * resets it to `Idle` a few seconds after every save, so an untouched or recently-saved document
 * would hang forever. While `Saving`/`Retrying`, waits up to `kWaitForSaveSettledCapMs`, then gives
 * up — the server's completion status is the real fallback, not this wait.
 */
export function waitForSaveSettled(document) {
    if (isSettled(document))
        return Promise.resolve();
    return new Promise(resolve => {
        let settled = false;
        // No-op default so `finish` always has something to call. `when` can invoke its effect
        // synchronously if the predicate is already true, which would otherwise call disposeWhen
        // before `when()` below has assigned it.
        let disposeWhen = () => { };
        function finish() {
            if (settled)
                return;
            settled = true;
            disposeWhen();
            clearTimeout(timer);
            resolve();
        }
        const timer = setTimeout(finish, kWaitForSaveSettledCapMs);
        disposeWhen = when(() => isSettled(document), finish);
    });
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoid2FpdC1mb3Itc2F2ZS1zZXR0bGVkLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9kb2N1bWVudC93YWl0LWZvci1zYXZlLXNldHRsZWQudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUEsT0FBTyxFQUFFLElBQUksRUFBRSxNQUFNLE1BQU0sQ0FBQztBQUM1QixPQUFPLEVBQXFCLFNBQVMsRUFBRSxNQUFNLFlBQVksQ0FBQztBQUUxRCxxRkFBcUY7QUFDckYsTUFBTSxDQUFDLE1BQU0sd0JBQXdCLEdBQUcsSUFBSSxDQUFDO0FBRTdDLFNBQVMsU0FBUyxDQUFDLFFBQTJCO0lBQzVDLE9BQU8sUUFBUSxDQUFDLFNBQVMsS0FBSyxTQUFTLENBQUMsTUFBTSxJQUFJLFFBQVEsQ0FBQyxTQUFTLEtBQUssU0FBUyxDQUFDLFFBQVEsQ0FBQztBQUM5RixDQUFDO0FBRUQ7Ozs7Ozs7O0dBUUc7QUFDSCxNQUFNLFVBQVUsa0JBQWtCLENBQUMsUUFBMkI7SUFDNUQsSUFBSSxTQUFTLENBQUMsUUFBUSxDQUFDO1FBQUUsT0FBTyxPQUFPLENBQUMsT0FBTyxFQUFFLENBQUM7SUFFbEQsT0FBTyxJQUFJLE9BQU8sQ0FBTyxPQUFPLENBQUMsRUFBRTtRQUNqQyxJQUFJLE9BQU8sR0FBRyxLQUFLLENBQUM7UUFDcEIsdUZBQXVGO1FBQ3ZGLHlGQUF5RjtRQUN6Rix5Q0FBeUM7UUFDekMsSUFBSSxXQUFXLEdBQUcsR0FBRyxFQUFFLEdBQUUsQ0FBQyxDQUFDO1FBRTNCLFNBQVMsTUFBTTtZQUNiLElBQUksT0FBTztnQkFBRSxPQUFPO1lBQ3BCLE9BQU8sR0FBRyxJQUFJLENBQUM7WUFDZixXQUFXLEVBQUUsQ0FBQztZQUNkLFlBQVksQ0FBQyxLQUFLLENBQUMsQ0FBQztZQUNwQixPQUFPLEVBQUUsQ0FBQztRQUNaLENBQUM7UUFFRCxNQUFNLEtBQUssR0FBRyxVQUFVLENBQUMsTUFBTSxFQUFFLHdCQUF3QixDQUFDLENBQUM7UUFDM0QsV0FBVyxHQUFHLElBQUksQ0FBQyxHQUFHLEVBQUUsQ0FBQyxTQUFTLENBQUMsUUFBUSxDQUFDLEVBQUUsTUFBTSxDQUFDLENBQUM7SUFDeEQsQ0FBQyxDQUFDLENBQUM7QUFDTCxDQUFDIn0=